import { Box } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import "./translations";

import type { ValuationHubCoreModelRealEstateProsaWebFactorsInput } from "@/api/generatedApi";
import { IndicativeMarketValuationIcon } from "@/assets";
import { PanelHeader } from "@/components/panelHeader";
import { CurrentValuationState } from "@/pages/valuationPackage/valuationDetailsPage/currentValuationSlice";
import { ValuationReportState } from "@/pages/valuationReport/valuationReportPage/valuationReportSlice";
import { colors } from "@/theme/tokens/colors";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

import { ApproachContent } from "./ApproachContent";
import { LandValueAssessment } from "./LandValueAssessment";

export interface IndicativeApproachProps {
  valuationState: ValuationReportState | CurrentValuationState;
  isLoading?: boolean;
  onSaveFactor?: (factor: number | ValuationHubCoreModelRealEstateProsaWebFactorsInput) => Promise<void> | void;
  isSaving?: boolean;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  panelContainer: {
    display: "grid",
    backgroundColor: "white",
    padding: theme.analysisLayout.panelPadding,
    gap: theme.analysisLayout.panelGap,
  },
  headerSection: {
    display: "grid",
    gap: theme.analysisLayout.compactGap,
  },
});

export const IndicativeMarketValuationPanel = ({
  valuationState,
  isLoading = false,
  onSaveFactor,
  isSaving = false,
}: IndicativeApproachProps): JSX.Element => {
  const { t } = useTranslation("IndicativeMarketValuationPanel");
  const theme = useTheme();
  const styles = createStyles(theme);
  const objectType = valuationState.propertyInformation?.generalInfo.objectType as ObjectTypeEnum | undefined;

  const approachProps: IndicativeApproachProps = {
    valuationState,
    isLoading,
    onSaveFactor,
    isSaving,
  };

  return (
    <Box sx={styles.panelContainer}>
      <Box sx={styles.headerSection}>
        <PanelHeader
          title={t("title")}
          icon={
            <IndicativeMarketValuationIcon
              sx={{ fontSize: theme.analysisLayout.headerIconSize, color: colors.brand.brownDark }}
            />
          }
        />
      </Box>

      {objectType === ObjectTypeEnum.plotOfLand ? (
        <LandValueAssessment {...approachProps} />
      ) : (
        <ApproachContent {...approachProps} objectType={objectType} />
      )}
    </Box>
  );
};
